import { useEffect, useId, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Filter } from "lucide-react"

import type { MetricFilterBounds } from "../lib/metricFilters"
import { hasMetricFilterBounds } from "../lib/metricFilters"

/** Active Today-button purple from Ads Manager date presets. */
const ACTIVE_FILTER_COLOR = "#7488e8"

interface ColumnMetricFilterProps {
  columnId: string
  label: string
  bounds: MetricFilterBounds | undefined
  onApply: (bounds: MetricFilterBounds | null) => void
}

function parseOptionalNumber(raw: string): number | undefined {
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : undefined
}

export function ColumnMetricFilter({
  columnId,
  label,
  bounds,
  onApply,
}: ColumnMetricFilterProps) {
  const active = hasMetricFilterBounds(bounds)
  const [open, setOpen] = useState(false)
  const [minText, setMinText] = useState("")
  const [maxText, setMaxText] = useState("")
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(
    null,
  )
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    setMinText(bounds?.min != null ? String(bounds.min) : "")
    setMaxText(bounds?.max != null ? String(bounds.max) : "")
  }, [open, bounds?.min, bounds?.max])

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setPanelPos(null)
      return
    }
    const place = () => {
      const rect = buttonRef.current!.getBoundingClientRect()
      const width = 220
      const left = Math.min(
        Math.max(8, rect.right - width),
        window.innerWidth - width - 8,
      )
      setPanelPos({ top: rect.bottom + 6, left })
    }
    place()
    window.addEventListener("scroll", place, true)
    window.addEventListener("resize", place)
    return () => {
      window.removeEventListener("scroll", place, true)
      window.removeEventListener("resize", place)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return
      }
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  const handleApply = () => {
    let min = parseOptionalNumber(minText)
    let max = parseOptionalNumber(maxText)
    if (min == null && max == null) {
      onApply(null)
      setOpen(false)
      return
    }
    if (min != null && max != null && min > max) {
      ;[min, max] = [max, min]
    }
    const next: MetricFilterBounds = {}
    if (min != null) next.min = min
    if (max != null) next.max = max
    onApply(next)
    setOpen(false)
  }

  const handleClear = () => {
    onApply(null)
    setOpen(false)
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={active ? `Edit ${label} filter` : `Filter ${label}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm text-white/45 hover:text-white/80"
        style={active ? { color: ACTIVE_FILTER_COLOR } : undefined}
      >
        <Filter className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
      </button>

      {open &&
        panelPos &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-labelledby={titleId}
            className="fixed z-[80] w-[220px] rounded-md border border-[#555] bg-[#2d2d2d] p-3 shadow-lg"
            style={{ top: panelPos.top, left: panelPos.left }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div id={titleId} className="mb-2 text-xs font-semibold text-white">
              {label}
            </div>
            <div className="mb-3 grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium uppercase tracking-wide text-white/55">
                  Min
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={minText}
                  onChange={(e) => setMinText(e.target.value)}
                  placeholder="Any"
                  className="h-8 rounded border border-[#555] bg-[#252525] px-2 text-xs text-white placeholder:text-white/35 focus:border-[#7488e8] focus:outline-none"
                  data-testid={`metric-filter-min-${columnId}`}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-medium uppercase tracking-wide text-white/55">
                  Max
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={maxText}
                  onChange={(e) => setMaxText(e.target.value)}
                  placeholder="Any"
                  className="h-8 rounded border border-[#555] bg-[#252525] px-2 text-xs text-white placeholder:text-white/35 focus:border-[#7488e8] focus:outline-none"
                  data-testid={`metric-filter-max-${columnId}`}
                />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleClear}
                className="h-7 rounded px-2 text-xs text-white/70 hover:text-white"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="h-7 rounded border border-[#8fa0ef]/35 bg-[#7488e8] px-3 text-xs font-medium text-white hover:brightness-110"
              >
                Apply
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
